"use client";

import { useContext, useRef, Children, useState, useEffect, forwardRef } from "react";
import EdenContextualMenu from "@churchofjesuschrist/eden-contextual-menu";
import PropTypes from "prop-types";
import styled from "styled-components";
import { ArrowDropDown } from "@churchofjesuschrist/eden-icons";
import { sharedInputCss, internalUseOnlyOpenDetailsStyles } from "../utils/sharedCss";
import { spacing8, spacing4 } from "@churchofjesuschrist/eden-style-constants";
import { ValidityStateContext, SmallContext, IdContext, DescribedByContext, DisabledContext } from "../utils/context";
import Label from "./Label";
import Checkbox from "./Checkbox";
import EdenFieldSet from "./FieldSet";
import { AppearanceContext } from "@churchofjesuschrist/eden-appearance";
import { useHasOverflow } from "@churchofjesuschrist/eden-has-overflow";
import { mergeRefs } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const InternalUseOnlySelect = styled.select.withConfig({
  componentId: "sc-11vq36o-0"
})(["", ";margin:0;appearance:none;padding:", ";", ";", ";&:disabled{opacity:1;}"], sharedInputCss, _ref => {
  let {
    $small
  } = _ref;
  return $small ? `${spacing4} ${spacing8}` : spacing8;
}, internalUseOnlyOpenDetailsStyles, _ref2 => {
  let {
    $hasOverflow,
    multiple
  } = _ref2;
  return $hasOverflow && multiple ? `
    overflow: hidden;
    & > option {
      pointer-events: none;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  
    & > option:not(:first-of-type) {
      visibility: hidden;
    }
    ` : multiple ? `
    min-width: 14ch;
    ` : "";
});
const ContextualMenu = styled(EdenContextualMenu).withConfig({
  componentId: "sc-11vq36o-1"
})(["max-height:275px;min-width:200px;max-width:100%;"]);
const FieldSet = styled(EdenFieldSet).withConfig({
  componentId: "sc-11vq36o-2"
})(["& > div{width:100%;}"]);

/**
 * The Select component allows users to choose a single option from a list of options that appears when the form is activated.
 */
const Select = /*#__PURE__*/forwardRef((_ref3, forwardedRef) => {
  let {
    "aria-describedby": ariaDescribedBy,
    children,
    defaultValue,
    id,
    multiple,
    small,
    value,
    disabled,
    ...props
  } = _ref3;
  const validityStateContext = useContext(ValidityStateContext);
  const smallContext = useContext(SmallContext);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  const curAppearanceContext = useContext(AppearanceContext);
  const disabledContext = useContext(DisabledContext);
  const internalRef = useRef();
  const ref = forwardedRef ?? internalRef;
  const [open, setOpen] = useState(false);
  const fieldSetRef = useRef(null);
  const [activeChild, setActiveChild] = useState(1);
  const [optionValues, setOptionValues] = useState(value ?? defaultValue ?? []);
  const [overflowRef, {
    hasY
  }] = useHasOverflow(children, {
    startY: true
  });
  const hasOverflow = hasY && multiple;
  useEffect(() => {
    const closestForm = ref.current.form;
    if (!closestForm) {
      return;
    }
    const resetHandler = () => {
      setOptionValues(value ?? defaultValue ?? []);
    };
    closestForm.addEventListener("reset", resetHandler);
    return () => closestForm.removeEventListener("reset", resetHandler);
  });
  useEffect(() => {
    setOptionValues(value ?? defaultValue ?? []);
  }, [value, defaultValue]);
  useEffect(() => {
    if (open === true) {
      const fieldSet = fieldSetRef.current;
      if (!fieldSet) {
        return;
      }
      const firstChecked = fieldSet.querySelector(`input:checked`);
      const animationFrame = requestAnimationFrame(() => {
        if (firstChecked) {
          firstChecked.focus();
          setActiveChild(Array.from(firstChecked.parentNode.parentNode.children).indexOf(firstChecked.parentNode) + 1);
        } else {
          fieldSet.querySelector(`label:nth-child(1)`).focus();
          setActiveChild(1);
        }
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [open]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(InternalUseOnlySelect, {
      "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
      defaultValue: defaultValue,
      id: id ? id : idContext,
      multiple: multiple,
      ref: multiple ? mergeRefs(overflowRef, ref) : ref,
      $small: small ? small : smallContext,
      disabled: disabled ? disabled : disabledContext,
      $validityState: validityStateContext,
      $icon: /*#__PURE__*/_jsx(ArrowDropDown, {}),
      value: value,
      $curAppearanceContext: curAppearanceContext,
      $hasOverflow: hasOverflow,
      ...(hasOverflow ? {
        onMouseDown: event => {
          event.preventDefault();
        },
        onClick: event => {
          event.preventDefault();
          setOpen(true);
        },
        onKeyDown: event => {
          if (event.code === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
          if (event.code === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
          if (event.code === "Space") {
            event.preventDefault();
            setOpen(true);
          }
        },
        onScroll: event => {
          event.preventDefault();
          event.target.scrollTop = 0;
        }
      } : {}),
      ...props,
      children: [hasOverflow ? /*#__PURE__*/_jsx("option", {
        value: "eden-multiple-select-display-value",
        children: Children.toArray(children).filter(child => {
          if (typeof child.props.value !== "undefined") {
            return optionValues.includes(child.props.value);
          } else {
            return optionValues.includes(child.props.children);
          }
        }).map(child => child.props.children || child.props.value).join(", ")
      }) : null, children]
    }), hasOverflow && ref.current ? /*#__PURE__*/_jsx(ContextualMenu, {
      forRef: ref,
      style: {
        width: ref.current.offsetWidth + "px"
      },
      arrow: "none",
      open: open,
      onClickOutside: () => {
        setOpen(false);
      },
      onKeyDown: event => {
        const fieldSet = fieldSetRef.current;
        if (event.code === "ArrowDown") {
          event.preventDefault();
          if (activeChild < fieldSet.querySelectorAll("label").length) {
            const newActiveChild = activeChild + 1;
            fieldSet.querySelector(`label:nth-child(${newActiveChild})`).focus();
            setActiveChild(newActiveChild);
          }
        }
        if (event.code === "ArrowUp") {
          event.preventDefault();
          if (activeChild > 1) {
            const newActiveChild = activeChild - 1;
            fieldSet.querySelector(`label:nth-child(${newActiveChild})`).focus();
            setActiveChild(newActiveChild);
          }
        }
        if (event.code === "Escape") {
          event.preventDefault();
          setOpen(false);
          ref.current.focus();
        }
        if (event.code === "Enter") {
          event.preventDefault();
          fieldSet.querySelector(`label:nth-child(${activeChild})`).click();
        }
        if (event.code === "Tab") {
          setOpen(false);
        }
      },
      children: /*#__PURE__*/_jsx(FieldSet, {
        ref: fieldSetRef,
        children: Children.toArray(children).map((_ref4, index) => {
          let {
            props
          } = _ref4;
          const childValue = props.value || props.children;
          if (props.disabled) {
            return;
          }
          return /*#__PURE__*/_jsxs(Label, {
            children: [/*#__PURE__*/_jsx(Checkbox, {
              tabIndex: "-1",
              checked: optionValues.includes(childValue),
              onChange: event => {
                var _ref$current, _ref$current2, _ref$current3;
                setActiveChild(index + 1);
                const option = [...(((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.options) ?? [])].find(o => o.value === childValue);
                if (option) {
                  if (event.target.checked) {
                    option.selected = "selected";
                  } else {
                    option.selected = "";
                  }
                }
                setOptionValues([...(((_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.selectedOptions) ?? [])].map(option => option.value));
                (_ref$current3 = ref.current) === null || _ref$current3 === void 0 ? void 0 : _ref$current3.dispatchEvent(new Event("change", {
                  bubbles: true
                }));
              }
            }), props.children || childValue]
          }, childValue);
        })
      })
    }) : null]
  });
});
Select.displayName = "Select";
Select.propTypes = {
  /** Identifies the element (or elements) that describes the Select */
  "aria-describedby": PropTypes.string,
  /** Content to render inside the Select Component. Children should be <option> elements. */
  children: PropTypes.node.isRequired,
  /** Default value for uncontrolled use. If `multiple` is being used, this should be an array of values. */
  defaultValue: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  /** Makes the input not mutable, focusable, or even submitted with the form */
  disabled: PropTypes.bool,
  /** An identifier which must be unique in the whole document */
  id: PropTypes.string,
  /** Indicates that multiple options can be selected in the list */
  multiple: PropTypes.bool,
  /** Used to reference the input in JavaScript, or to reference form data after a form is submitted */
  name: PropTypes.string,
  /** Callback to execute when the value changes */
  onChange: PropTypes.func,
  /** Whether the input is required to be filled out to validate */
  required: PropTypes.bool,
  /** A boolean indicating if the Select should be rendered in a smaller height */
  small: PropTypes.bool,
  /** The value of the Select for controlled use. If `multiple` is being used, this should be an array of values. */
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
};
export default Select;